import React, { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  GenericMessagePreview,
  isProbablyHtml,
  stripHtml,
  WhatsAppBubblePreview,
} from "../../components/communication/MessagePreviewViews";
import { getCommunicationTemplateById } from "../../services/communicationTemplateApi";

export type ChannelStatus = "Active" | "Inactive" | "Paused";
export type PreviewChannelKey = "whatsapp" | "sms" | "rcs" | "email" | "inApp";

const looksLikeObjectId = (s: string) => /^[a-fA-F0-9]{24}$/.test(String(s ?? "").trim());

type Props = {
  status: ChannelStatus;
  channelKey: PreviewChannelKey;
  templateRef: string;
  statusClassName: string;
};

export function ActiveChannelStatusChip({
  status,
  channelKey,
  templateRef,
  statusClassName,
}: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rawBody, setRawBody] = useState<string | null>(null);

  useEffect(() => {
    if (!open || status !== "Active") return;
    const ref = String(templateRef ?? "").trim();
    if (!ref) {
      setRawBody(null);
      setLoadError("No template linked for this channel.");
      return;
    }
    if (!looksLikeObjectId(ref)) {
      setRawBody(
        `Template reference: ${ref}\n\nUse a saved template (24-character ID) to load a full preview here.`
      );
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setRawBody(null);

    getCommunicationTemplateById(ref)
      .then((t) => {
        if (cancelled) return;
        const body = typeof t?.body === "string" ? t.body : "";
        setRawBody(body.length ? body : "(empty template body)");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e && typeof e === "object" && "message" in e ? String((e as Error).message) : "Failed to load template");
        setRawBody(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, status, templateRef]);

  const previewParts = useMemo(() => {
    const body = (rawBody ?? "").trim();
    if (!body) return { fullHtml: null as string | null, fullPlain: "" };
    const asHtml = isProbablyHtml(body);
    return {
      fullHtml: asHtml ? body : null,
      fullPlain: asHtml ? stripHtml(body) : body,
    };
  }, [rawBody]);

  const isWhatsApp = channelKey === "whatsapp";

  const chipClass = `inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClassName}`;

  if (status !== "Active") {
    return <span className={chipClass}>{status}</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${chipClass} cursor-pointer transition hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EC6E51] focus-visible:ring-offset-1`}
          aria-label={`Preview ${channelKey} template`}
        >
          {status}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(100vw-2rem,20rem)] border-gray-200 p-0 shadow-lg data-[state=open]:z-[100]"
        align="end"
        side="bottom"
        sideOffset={6}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading template…</div>
        ) : loadError ? (
          <div className="p-4 text-sm text-rose-700">{loadError}</div>
        ) : rawBody == null ? (
          <div className="p-4 text-sm text-gray-600">No preview available.</div>
        ) : isWhatsApp ? (
          <WhatsAppBubblePreview
            fullHtml={previewParts.fullHtml}
            fullPlain={previewParts.fullPlain}
            sentAt={new Date().toISOString()}
          />
        ) : (
          <GenericMessagePreview fullHtml={previewParts.fullHtml} fullPlain={previewParts.fullPlain} />
        )}
      </PopoverContent>
    </Popover>
  );
}
